// The one time control Results, Responses and Overview share
// (results-responses-split spec §3, consistency-pass spec §5): kore's
// "Submitted" (Instant) and "Submitted within" (Duration) chips, at most one
// of them set, "in the last 30 days" when the URL names neither, and
// `submitted=all` to say the chip was removed on purpose. kore can't offer
// both kinds under one filter, so this sits between useKoreSearch and the
// router: it widens what kore reads (the default) and narrows what kore
// writes (one chip, the marker, and never the default itself).

import { parseDuration } from "@pej/kore/components";
import { useCallback, useMemo } from "react";

export const ALL_TIME_KEY = "submitted";
// Every Submitted key kore parses into a chip the server takes: the picker
// writes Gte, Lt and Between, but a hand-typed Lte or Gt is a chip too and
// must count for the one-chip rule. (Eq and Neq have no suffix, and kore
// drops a bare Instant key.)
const INSTANT_KEYS = [
    "submittedAt_Lt",
    "submittedAt_Lte",
    "submittedAt_Gt",
    "submittedAt_Gte",
    "submittedAt_Between",
] as const;
const DURATION_KEYS = ["submittedWithin", "submittedWithin_Last"] as const;
const TIME_KEYS = [...INSTANT_KEYS, ...DURATION_KEYS] as const;
export const DEFAULT_WITHIN = "30d";

export type Preset = "24h" | "7d" | "30d" | "90d" | "all";
export const PRESETS: readonly { key: Preset; label: string }[] = [
    { key: "24h", label: "24 hours" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "90d", label: "90 days" },
    { key: "all", label: "All time" },
];

export type SearchPatch = Record<string, string | undefined>;

function has(dict: Record<string, unknown>, keys: readonly string[]): boolean {
    return keys.some((key) => dict[key] !== undefined);
}

function family(dict: Record<string, unknown>, keys: readonly string[]): string {
    return keys.map((key) => String(dict[key] ?? "")).join("|");
}

/** The Duration chip's value, canonical key first. */
export function durationValue(dict: Record<string, unknown>): string | undefined {
    const value = dict.submittedWithin_Last ?? dict.submittedWithin;
    return typeof value === "string" ? value : undefined;
}

/** Whether a Submitted within value is the default's length (`720h` is). */
function isDefaultWithin(value: string | undefined): boolean {
    return value !== undefined && parseDuration(value) === parseDuration(DEFAULT_WITHIN);
}

/** What kore reads: the URL, plus the 30-day chip when it names no time and
 *  the chip wasn't removed on purpose. */
export function withDefaultTime(search: Record<string, unknown>): Record<string, unknown> {
    if (search[ALL_TIME_KEY] === "all" || has(search, TIME_KEYS)) return search;
    return { ...search, submittedWithin_Last: DEFAULT_WITHIN };
}

/**
 * What reaches the URL from a kore patch. Only for kore's own patches: they
 * carry every manifest key (`buildUrlPatch` wipes each def, then overlays the
 * chips), so a time key missing from `patch` means that chip is gone. `prev`
 * is what kore read (`withDefaultTime` of the URL).
 */
export function timePatch(prev: Record<string, unknown>, patch: SearchPatch): SearchPatch {
    const next: SearchPatch = { ...patch };
    if (has(next, INSTANT_KEYS) && has(next, DURATION_KEYS)) {
        // Both kinds set: keep the one the user just changed. Neither changed
        // only when a hand-edited URL held both; Submitted within wins then.
        const instantChanged = family(next, INSTANT_KEYS) !== family(prev, INSTANT_KEYS);
        for (const key of instantChanged ? DURATION_KEYS : INSTANT_KEYS) next[key] = undefined;
    }
    if (!has(next, INSTANT_KEYS) && isDefaultWithin(durationValue(next))) {
        // The default stays implicit: no time key means 30 days.
        for (const key of DURATION_KEYS) next[key] = undefined;
        next[ALL_TIME_KEY] = undefined;
        return next;
    }
    next[ALL_TIME_KEY] = has(next, TIME_KEYS) ? undefined : "all";
    return next;
}

/** The preset the current time chip equals, by length for `Last` (so `1d`
 *  is "24 hours"); `null` for a custom chip. */
export function presetOf(search: Record<string, unknown>): Preset | null {
    const timed = withDefaultTime(search);
    if (has(timed, INSTANT_KEYS)) return null;
    const value = durationValue(timed);
    if (value === undefined) return timed[ALL_TIME_KEY] === "all" ? "all" : null;
    const ms = parseDuration(value);
    if (ms === null) return null;
    return PRESETS.find((preset) => preset.key !== "all" && parseDuration(preset.key) === ms)?.key ?? null;
}

/** A preset click, straight to the router (not through `timePatch`). */
export function presetPatch(preset: Preset): SearchPatch {
    const patch: SearchPatch = Object.fromEntries(TIME_KEYS.map((key) => [key, undefined]));
    patch[ALL_TIME_KEY] = preset === "all" ? "all" : undefined;
    if (preset !== "all" && preset !== DEFAULT_WITHIN) patch.submittedWithin_Last = preset;
    return patch;
}

/** useKoreSearch's router, with the time rules applied both ways. */
export function useTimeChipRouter(
    rawSearch: Record<string, unknown>,
    onSearchPatch: (patch: SearchPatch) => void,
): { search: Record<string, unknown>; onChange: (patch: SearchPatch) => void } {
    const search = useMemo(() => withDefaultTime(rawSearch), [rawSearch]);
    const onChange = useCallback(
        (patch: SearchPatch) => onSearchPatch(timePatch(search, patch)),
        [search, onSearchPatch],
    );
    return useMemo(() => ({ search, onChange }), [search, onChange]);
}
